import {
  featureSchema,
  guidanceFieldFeaturesSchema,
  lineStringSchema,
  pointSchema
} from 'api-validations/geojson-schemas'
import { z } from 'zod'

export enum GuidancePrimitive {
  /**
   * A straight line defined by two points.
   */
  ABLine = 'ABLine',
  /**
   * A point defined by a location and a heading.
   */
  AHeading = 'AHeading',
  /**
   * A line feature that consists of multiple discrete points, approximating a curve
   */
  Curve = 'Curve',
  /**
   * A circular pivot defined by a center point and a radius.
   */
  RadialPivot = 'RadialPivot',
  /**
   * Only used for pivot features that are derived from a driven curve, where
   * the user physically drove part of the pivot and wants to extrapolate from
   * that instead of manually entering a center and radius.
   */
  CurveDerivedPivot = 'CurveDerivedPivot',
  /**
   * One line of a field already propagated by an external system. The display
   * drives it as given, so it is never propagated or used as a region's infill.
   */
  PrePropagated = 'PrePropagated'
}

const propagatableGuidancePrimitiveTypes = z
  .enum(GuidancePrimitive)
  .exclude([GuidancePrimitive.PrePropagated])

export enum HeadlandCornerType {
  /**
   * The implement will drive straight through the corner instead of curving through it
   */
  Square = 'Square',
  /**
   * The implement will curve through the corner, creating a rounded headland boundary
   */
  Round = 'Round'
}

export enum GenerationScope {
  /**
   * Only generates headland lines.
   */
  HeadlandsOnly = 'HeadlandsOnly',
  /**
   * Leaves space for headlands but does not include them in the line propagation.
   */
  InfillOnly = 'InfillOnly',
  Both = 'Both'
}

export enum BoundaryHoleBehavior {
  /**
   * The implement *can* drive through the interior holes and resume
   * application on the other side.
   */
  Passable = 'Passable',
  /**
   * The implement *should not* drive through the interior holes.
   */
  Impassable = 'Impassable'
}

export enum TurnType {
  Flat = 'Flat',
  Bulb = 'Bulb',
  YTurn = 'YTurn',
  KTurn = 'KTurn',
  Headland = 'Headland',
  Keyhole = 'Keyhole',
  ExtendedU = 'ExtendedU',
  PTurn = 'PTurn',
  Auto = 'Auto',
  EfgYTurn = 'EfgYTurn',
  EfgKTurn = 'EfgKTurn'
}

export enum TurnDirection {
  Auto = 'Auto',
  Left = 'Left',
  Right = 'Right'
}

export enum CoverageOrder {
  HeadlandsFirst = 'HeadlandsFirst',
  HeadlandsLast = 'HeadlandsLast',
  Interleaved = 'Interleaved'
}

export enum HeadlandOrder {
  OuterToInner = 'OuterToInner',
  InnerToOuter = 'InnerToOuter'
}

export enum HeadlandRotation {
  Clockwise = 'Clockwise',
  CounterClockwise = 'CounterClockwise'
}

export enum OffsetDirection {
  /**
   * Offsets line propagation to the left when facing in the direction of the
   * feature geometry. For circles, orientation is assumed to be clockwise, so
   * a left offset will move lines outward.
   */
  Left = 'Left',
  /**
   * Offsets line propagation to the right when facing in the direction of the
   * feature geometry. For circles, orientation is assumed to be clockwise, so
   * a right offset will move lines inward.
   */
  Right = 'Right'
}

export enum PropagationMode {
  /**
   * Take a single feature and propagate it infinitely in all
   * directions. This is the simplest mode. The user just has
   * a single guidance line that they want to extend across
   * the field
   */
  Infinite = 'Infinite',
  /**
   * For each bounded region of the field, take a feature and
   * propagate it within that region only, inserting headlands
   * where appropriate per configuration.
   */
  RegionBounded = 'RegionBounded',
  /**
   * Partition the field using a Voronoi diagram based on the provided features.
   * Each point in the field will follow its closest feature.
   */
  VoronoiPartitioned = 'VoronoiPartitioned'
}

enum CurveSettingsType {
  /**
   * Heuristically derived from implement geometry
   */
  Auto = 'Auto',
  /**
   * Advanced, manual tweaking
   */
  Manual = 'Manual'
}

const guidanceFeaturePathSchema = z.object({
  /**
   * The number of passes to skip over when making a turn before starting the next.
   *
   * If not provided, defaults to 0, meaning the implement will turn immediately and start
   * the next pass right next to the previous one.
   */
  skipNumber: z.number().int().nonnegative().optional(),
  /**
   * If absent, defaults to TurnType.Bulb
   */
  turnType: z.enum(TurnType).optional(),
  /**
   * Settings determining how fast the implement can turn
   */
  curveSettings: z.discriminatedUnion('type', [
    z.object({
      type: z.literal(CurveSettingsType.Auto),
      /**
       * Larger implements have larger turn radii.
       */
      trailerLengthFt: z.number().nonnegative(),
      hitchOffsetFt: z.number().nonnegative()
    }),
    z.object({
      type: z.literal(CurveSettingsType.Manual),
      /**
       * The maximum curvature that the implement can handle while making a uniform,
       * continuous turn. This is essentially a rate of change of heading per unit distance,
       * and is directly correlated to the turn radius.
       */
      maxCurvature: z.number().nonnegative(),
      /**
       * The maximum rate at which the implement can change its curvature. This isn't
       * the turn radius but how quickly the turn radius changes. Whereas curvature is
       * how far the wheels can be turned at a given moment, curvature rate is how
       * quickly the operator can move the wheel from straight to fully turned.
       */
      maxCurvatureRate: z.number().nonnegative()
    })
  ]),
  /**
   * If not provide, defaults to Auto
   */
  turnDirection: z.enum(TurnDirection).optional()
})

const implementGuidancePropertiesSchema = z.object({
  swathWidthFt: z.number().nonnegative(),
  rowSpacingFt: z.number().nonnegative(),
  swathOffset: z.object({
    distanceFt: z.number(),
    direction: z.enum(OffsetDirection)
  })
})

type Position = number[]

const areCoordinatesEqual = (a: Position, b: Position): boolean => a[0] === b[0] && a[1] === b[1]

const fieldOriginSchema = z.object({
  geometry: pointSchema,
  properties: z.object({
    name: z.string().optional()
  })
})

/**
 * The primitive kinds Helm can propagate. `shared` is spread into each kind's
 * `properties.guidance`, alongside the fields that kind alone carries.
 */
const propagatablePrimitiveOptions = <TShared extends z.ZodRawShape>(shared: TShared) =>
  [
    z.object({
      type: z.literal(GuidancePrimitive.ABLine),
      primitive: featureSchema({
        geometry: lineStringSchema
          .refine((geom) => geom.coordinates.length === 2, {
            message: 'ABLine geometry must have exactly 2 points'
          })
          .refine(({ coordinates: [a, b] }) => !areCoordinatesEqual(a, b), {
            message: 'ABLine geometry points must be distinct'
          }),
        properties: z.object({
          guidance: z.object({ ...shared, type: z.literal(GuidancePrimitive.ABLine) })
        })
      })
    }),
    z.object({
      type: z.literal(GuidancePrimitive.AHeading),
      primitive: featureSchema({
        geometry: pointSchema,
        properties: z.object({
          guidance: z.object({
            ...shared,
            type: z.literal(GuidancePrimitive.AHeading),
            headingDeg: z.number().min(0).lt(360)
          })
        })
      })
    }),
    z.object({
      type: z.literal(GuidancePrimitive.Curve),
      primitive: featureSchema({
        geometry: lineStringSchema,
        properties: z.object({
          guidance: z.object({ ...shared, type: z.literal(GuidancePrimitive.Curve) })
        })
      })
    }),
    z.object({
      type: z.literal(GuidancePrimitive.RadialPivot),
      primitive: featureSchema({
        geometry: pointSchema,
        properties: z.object({
          guidance: z.object({
            ...shared,
            type: z.literal(GuidancePrimitive.RadialPivot),
            radiusFt: z.number().positive()
          })
        })
      })
    }),
    z.object({
      type: z.literal(GuidancePrimitive.CurveDerivedPivot),
      primitive: featureSchema({
        geometry: lineStringSchema,
        properties: z.object({
          guidance: z.object({ ...shared, type: z.literal(GuidancePrimitive.CurveDerivedPivot) })
        })
      })
    })
  ] as const

/**
 * Every guidance primitive kind, with `shared` spread into each kind's
 * `properties.guidance` as in {@link propagatablePrimitiveOptions}.
 */
export const guidancePrimitiveUnion = <TShared extends z.ZodRawShape>(shared: TShared) =>
  z.discriminatedUnion('type', [
    ...propagatablePrimitiveOptions(shared),
    z.object({
      type: z.literal(GuidancePrimitive.PrePropagated),
      primitive: featureSchema({
        geometry: lineStringSchema,
        properties: z.object({
          guidance: z.object({ ...shared, type: z.literal(GuidancePrimitive.PrePropagated) })
        })
      })
    })
  ])

export const guidancePrimitiveSchema = guidancePrimitiveUnion(
  implementGuidancePropertiesSchema.shape
)

export const propagatableGuidancePrimitiveSchema = z.discriminatedUnion(
  'type',
  propagatablePrimitiveOptions(implementGuidancePropertiesSchema.shape)
)

export type PropagatableGuidancePrimitive = z.infer<typeof propagatableGuidancePrimitiveSchema>

export const guidanceRefSchema = z.object({
  type: z.enum(GuidancePrimitive),
  name: z.string(),
  id: z.uuid()
})

const infillRegionSchema = z.object({
  /**
   * The region that will enclose the infill feature's propagation.
   * The id points to the feature id in the boundary, if present.
   * If absent, the region covers the entire area of the field.
   *
   */
  regionId: z.string().optional(),
  /**
   * The feature to use for guidance line generation within the bounded region.
   * This will be propagated infinitely, and then clipped to the boundary + headlands.
   */
  infill: propagatableGuidancePrimitiveSchema
})

/**
 * Globally applied settings, can eventually support local overrides, per edge, corner, or hole.
 *
 * The field is defined by the union of all region features, so edges that are
 * contiguous will be merged into a single feature, avoiding internal headlands if the edge is
 * genuinely shared.
 */
const globalGuidanceSettingsSchema = z.object({
  cornerType: z.enum(HeadlandCornerType),
  exteriorHeadlandCount: z.number().int().nonnegative(),
  interiorHeadlandCount: z.number().int().nonnegative(),
  interiorHeadlandOrder: z.enum(HeadlandOrder),
  exteriorHeadlandOrder: z.enum(HeadlandOrder),
  interiorHeadlandRotation: z.enum(HeadlandRotation),
  exteriorHeadlandRotation: z.enum(HeadlandRotation),
  coverageOrder: z.enum(CoverageOrder),
  generationScope: z.enum(GenerationScope),
  boundaryHoleBehavior: z.enum(BoundaryHoleBehavior),
  /**
   * If included, the guidance generation will create a full implement itinerary,
   * from entrance to exit, with turns and headlands as appropriate.
   *
   * If omitted, the system will not generate a full path, just lines.
   */
  path: guidanceFeaturePathSchema.optional(),
  /**
   * If included, this is the origin point which will be used as the reference point
   * for all local coordinate calculations.
   *
   * If omitted, the system will use the first point of the first feature of the
   * field features (RegionBounded), or the first point of the first feature of the features array (Infinite),
   * as the origin.
   */
  fieldOrigin: fieldOriginSchema.optional()
})

const infiniteHelmRequestSchema = z.object({
  type: z.literal(PropagationMode.Infinite),
  feature: propagatableGuidancePrimitiveSchema,
  globalSettings: globalGuidanceSettingsSchema
})

const regionBoundedHelmRequestSchema = z.object({
  type: z.literal(PropagationMode.RegionBounded),
  /**
   * The inspected structure of the field — regions, edges, and corners — rather
   * than the raw boundary. The overrides and infill regions below reference these
   * features by their stable ids.
   */
  fieldFeatures: guidanceFieldFeaturesSchema,
  globalSettings: globalGuidanceSettingsSchema,
  /**
   * Overrides keyed by the ids of the field features above:
   *  - edges: per-edge headland settings
   *  - corners: per-corner turn-type settings
   *  - holes: how each hole is treated for propagation
   *  - corridors: fold a detected corridor (a waterway or lane splitting the
   *    field into two regions) into the field; see `boundary_corridors` on
   *    the inspect response
   */
  overrides: z.object({
    edges: z
      .array(
        z.object({
          id: z.string(),
          headlandCount: z.number().int().nonnegative(),
          headlandRotation: z.enum(HeadlandRotation),
          headlandOrder: z.enum(HeadlandOrder)
        })
      )
      .optional(),

    holes: z
      .array(
        z.object({
          id: z.string(),
          behavior: z.enum(BoundaryHoleBehavior)
        })
      )
      .optional(),

    corners: z
      .array(
        z.object({
          id: z.string(),
          cornerType: z.enum(HeadlandCornerType)
        })
      )
      .optional(),

    /**
     * Melds the two regions a corridor joins across its strip, so the exterior
     * headland runs continuously around both. `Passable` drives straight across
     * the strip with no headlands on either bank (every region joined must then
     * share one infill); `Impassable` routes around it like any hole, with the
     * global interior headland count.
     */
    corridors: z
      .array(
        z.object({
          id: z.string(),
          behavior: z.enum(BoundaryHoleBehavior)
        })
      )
      .optional()
  }),
  /**
   * Infill behavior on a per-region basis.
   */
  infillRegions: z
    .array(infillRegionSchema)
    .min(1)
    .refine((regions) => regions.every((r) => r.regionId !== undefined || regions.length === 1)) // either a global setting or a per-region setting must be provided
})

// NOTE: this is not implemented yet and is placeholder for the eventual implementation schema
const _voronoiPartitionedHelmRequestSchema = z.object({
  type: z.literal(PropagationMode.VoronoiPartitioned),
  fieldFeatures: guidanceFieldFeaturesSchema.optional(),
  globalSettings: globalGuidanceSettingsSchema.optional(),
  features: z.array(propagatableGuidancePrimitiveSchema)
})

export const helmRequestSchema = z.discriminatedUnion('type', [
  regionBoundedHelmRequestSchema,
  infiniteHelmRequestSchema
])

export type RegionBoundedHelmRequest = z.infer<typeof regionBoundedHelmRequestSchema>

/**
 * An ordered set of lines the operator can switch between in the cab. A plan's
 * guidance plan follows its own infill lines whether or not its line sets
 * include them.
 */
export const lineSetSchema = z.object({
  primitives: z.array(guidanceRefSchema).min(1)
})

export type LineSet = z.infer<typeof lineSetSchema>

/**
 * A guidance plan without its field features: the propagation settings,
 * overrides, and the line each region follows.
 */
export const guidancePlanSummarySchema = z.object({
  ...regionBoundedHelmRequestSchema.omit({ type: true, fieldFeatures: true }).shape,
  /**
   * The boundary asset the field features were inspected from, so a client can
   * tell when the plan using this guidance plan has a different boundary.
   */
  boundaryId: z.uuid(),
  implementWidth: z.number().positive(),
  rowSpacing: z.number().positive(),
  infillRegions: z
    .array(
      infillRegionSchema.extend({
        infill: guidanceRefSchema.extend({ type: propagatableGuidancePrimitiveTypes })
      })
    )
    .min(1)
})

export type GuidancePlanSummary = z.infer<typeof guidancePlanSummarySchema>

/**
 * Covers a whole field: each region of the inspected field follows one line,
 * with headlands, corners, holes, and corridors as configured, so the operator
 * never switches lines by hand.
 */
export const guidancePlanSchema = guidancePlanSummarySchema.extend({
  fieldFeatures: guidanceFieldFeaturesSchema
})

export type GuidancePlan = z.infer<typeof guidancePlanSchema>

export const DEFAULT_GLOBAL_SETTINGS = {
  cornerType: HeadlandCornerType.Round,
  exteriorHeadlandCount: 2,
  interiorHeadlandCount: 1,
  interiorHeadlandOrder: HeadlandOrder.OuterToInner,
  exteriorHeadlandOrder: HeadlandOrder.OuterToInner,
  interiorHeadlandRotation: HeadlandRotation.Clockwise,
  exteriorHeadlandRotation: HeadlandRotation.Clockwise,
  coverageOrder: CoverageOrder.HeadlandsFirst,
  generationScope: GenerationScope.Both,
  boundaryHoleBehavior: BoundaryHoleBehavior.Impassable
} satisfies z.infer<typeof globalGuidanceSettingsSchema>

export const DEFAULT_GUIDANCE_PROPERTIES = {
  swathWidthFt: 30,
  rowSpacingFt: 2.5,
  swathOffset: {
    distanceFt: 0,
    direction: OffsetDirection.Left
  }
} satisfies z.infer<typeof implementGuidancePropertiesSchema>
